import {groupByUniqueKey} from '@benchling/js-utils';
import {Switch} from '@mantine/core';
import {css, StyleSheet, StyleDeclaration} from 'aphrodite';
import React from 'react';

import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import {SequencePrime} from 'biojs/BioConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP} from 'constants/SequenceConstants';
import {SuccessReturnType} from 'graphql-client/BenchlingQuery';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {SchemaCompatibilityQuery} from 'molbio/bulk-assembly/assemble-modal/__generated/SchemaCompatibilityQuery';
import {
  FieldMappingTarget,
  FieldMappingType,
  PerformAssemblyModalState,
  PerformAssemblyModalAction,
} from 'molbio/bulk-assembly/assemble-modal/performAssemblyModalReducer';
import {
  AssemblyEntityCountsByModelType,
  FragmentDetails,
  RawOutputPolymerCount,
  UnknownInputPolymers,
  SchemaIdToImplementedInterfaces,
  mergeAssemblyEntityCountsByModelType,
} from 'molbio/bulk-assembly/assemble-modal/polymerSchemaLinkCompatibility';
import SchemaFieldMapper, {
  SectionOptions,
  SingleOption,
} from 'molbio/bulk-assembly/assemble-modal/SchemaFieldMapper';
import {useSchemaInterfaces} from 'molbio/bulk-assembly/assemble-modal/useSchemaInterfaces';
import {BinDetails} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {getConstructsSwatchesStyles} from 'molbio/bulk-assembly/card/BulkAssemblyCard';
import {getSequenceSubtype} from 'molbio/SeqUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_13,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

export function OutputSection({
  children,
  extraSwatchStyles,
  title,
  switchDetails,
  icon,
  entityCount,
  entityName,
  disabled = false,
}: {
  children: React.ReactNode;
  extraSwatchStyles?: StyleDeclaration;
  title: string;
  switchDetails?: {checked: boolean; onCheck: () => void; tooltipText: string};
  icon: BenchlingIconName;
  entityCount: number;
  entityName: string;
  disabled?: boolean;
}): JSX.Element {
  const checked = switchDetails?.checked ?? true;
  const isDisabled = disabled || !checked;
  return (
    <div
      className={css(styles.outputSection, isDisabled && styles.disabledOutputSection)}
      {...getTestProps({
        component: 'PerformAssemblyDefineOutputsPane',
        element: 'outputSection',
        key: title,
      })}
    >
      <div className={css(styles.swatch, extraSwatchStyles)} />
      <div className={css(styles.outputSectionContent)}>
        <div className={css(styles.outputSectionHeader)}>
          <div className={css(styles.outputSectionTitle)}>{title}</div>
          {switchDetails && (
            <Tooltip text={switchDetails.tooltipText}>
              <Switch
                checked={checked}
                onChange={switchDetails.onCheck}
                size="md"
                {...getTestProps({
                  component: 'PerformAssemblyDefineOutputsPane',
                  element: 'switch',
                  key: title,
                })}
              />
            </Tooltip>
          )}
        </div>
        <div className={css(styles.outputDetail)}>
          <div className={css(styles.countSection)}>
            <BenchlingIcon icon={icon} />
            <span className={css(styles.countText)}>
              {entityCount} {entityName}
            </span>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function getFragmentBinSwatchStyles(color: string | null): {fragmentBinSwatch: StyleDeclaration} {
  return StyleSheet.create({
    fragmentBinSwatch: {
      background: color ?? Colors.GRAY_DARK,
    },
  });
}

interface BinSectionProps {
  orderedBinDetails: Array<BinDetails>;
  selectedBinId: string | null;
  onBinChange: (binId: string | null) => void;
  disabled?: boolean;
}

export function BinSection({
  orderedBinDetails,
  selectedBinId,
  onBinChange,
  disabled = false,
}: BinSectionProps): JSX.Element {
  const selectedBin = orderedBinDetails.find(({binId}) => binId === selectedBinId);

  return (
    <div
      className={css(styles.dropdownSection)}
      {...getTestProps({component: 'PerformAssemblyDefineOutputsPane', element: 'binSection'})}
    >
      <div className={css(styles.dropdownTitle)}>Fragment bin</div>
      <div>
        <SelectDropdown
          displayName={selectedBin ? selectedBin.binName : 'All bins'}
          menu={
            <Menu width={MenuWidth.FULL} value={selectedBinId}>
              <Section>
                <Option
                  value={null}
                  onSelect={() => {
                    onBinChange(null);
                  }}
                >
                  All bins
                </Option>
              </Section>
              {orderedBinDetails.map(({binId, binName, binColor}) => {
                const binStyle = StyleSheet.create({borderColor: {borderLeftColor: binColor}});
                return (
                  <Option
                    key={binId}
                    value={binId}
                    onSelect={() => {
                      if (binId === selectedBinId) {
                        onBinChange(null);
                      } else {
                        onBinChange(binId);
                      }
                    }}
                    contentClassName={css(styles.fragmentDropdown, binStyle.borderColor)}
                  >
                    {binName}
                  </Option>
                );
              })}
            </Menu>
          }
          disabled={disabled}
          closeOnInsideClick={true}
          closeOnOutsideClick={true}
        />
      </div>
    </div>
  );
}

interface SchemaSectionProps {
  schemaId: string | null;
  onTagSchemaChange: (tagSchema: TagSchema | null) => void;
  disabled?: boolean;
  folderItemType: OligoAwareFolderItemType;
  testKey: string;
}

function SchemaSection({
  schemaId,
  onTagSchemaChange,
  disabled = false,
  folderItemType,
  testKey,
}: SchemaSectionProps): JSX.Element {
  const handleTagSchemaChange = (tagSchema: TagSchema | null): void => {
    onTagSchemaChange(tagSchema);
  };
  return (
    <div className={css(styles.dropdownSection)}>
      <div className={css(styles.dropdownTitle)}>Schema</div>
      <div>
        <TagSchemaSelector
          allowNone={true}
          selectedTagSchemaId={schemaId}
          onChange={handleTagSchemaChange}
          oligoAwareFolderItemTypes={[folderItemType]}
          isDisabled={disabled}
          requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
          dataTestAttrs={{
            component: 'PerformAssemblyDefineOutputsPane',
            element: 'schemaSelector',
            key: testKey,
          }}
        />
      </div>
    </div>
  );
}

function getOptionsForFragmentPrimerField(
  primerOutputDetails: RawOutputPolymerCount,
  primerDetailsByPrime: Record<
    SequencePrime,
    {preferredPrimers: UnknownInputPolymers; designedPrimers: RawOutputPolymerCount}
  >,
  schemaCompatibilityResult: SuccessReturnType<SchemaCompatibilityQuery>,
  primerTagSchemaId: string | null
): Array<SingleOption<FieldMappingTarget>> {
  return [
    {
      type: 'single-option',
      displayName: 'Fragment primers',
      value: {type: FieldMappingType.FRAGMENT_PRIMERS},
      entityModelDetails: mergeAssemblyEntityCountsByModelType([
        getAdjustedOutputDetailsByModelType(primerOutputDetails, primerTagSchemaId),
        getAdjustedInputDetailsByModelType(
          primerDetailsByPrime[SequencePrime.FIVE].preferredPrimers,
          schemaCompatibilityResult
        ),
        getAdjustedInputDetailsByModelType(
          primerDetailsByPrime[SequencePrime.THREE].preferredPrimers,
          schemaCompatibilityResult
        ),
      ]),
      key: 'fragment_primers',
      requiresMultiLink: true,
    },
    {
      type: 'single-option',
      displayName: "5' primers",
      value: {type: FieldMappingType.FIVE_PRIME_PRIMER},
      entityModelDetails: mergeAssemblyEntityCountsByModelType([
        getAdjustedOutputDetailsByModelType(
          primerDetailsByPrime[SequencePrime.FIVE].designedPrimers,
          primerTagSchemaId
        ),
        getAdjustedInputDetailsByModelType(
          primerDetailsByPrime[SequencePrime.FIVE].preferredPrimers,
          schemaCompatibilityResult
        ),
      ]),
      key: 'five_prime_primers',
      requiresMultiLink: false,
    },
    {
      type: 'single-option',
      displayName: "3' primers",
      value: {type: FieldMappingType.THREE_PRIME_PRIMER},
      entityModelDetails: mergeAssemblyEntityCountsByModelType([
        getAdjustedOutputDetailsByModelType(
          primerDetailsByPrime[SequencePrime.THREE].designedPrimers,
          primerTagSchemaId
        ),
        getAdjustedInputDetailsByModelType(
          primerDetailsByPrime[SequencePrime.THREE].preferredPrimers,
          schemaCompatibilityResult
        ),
      ]),
      key: 'three_prime_primers',
      requiresMultiLink: false,
    },
  ];
}

function getOptionsForConstructField(
  orderedBinDetails: Array<BinDetails>,
  fragmentDetailsByBin: Record<
    string,
    {inputDetails: AssemblyEntityCountsByModelType; outputDetails: AssemblyEntityCountsByModelType}
  >,
  saveFragments: boolean
): SectionOptions<FieldMappingTarget> {
  if (orderedBinDetails.length === 0) {
    return [];
  }

  const nestedOptions: SectionOptions<FieldMappingTarget> = orderedBinDetails.map((bin) => {
    const fragmentDetails = fragmentDetailsByBin[bin.binId];
    const options: Array<SingleOption<FieldMappingTarget>> = [
      {
        type: 'single-option' as const,
        displayName: 'Input fragments',
        value: {type: FieldMappingType.INPUT_FRAGMENT_BIN, binId: bin.binId},
        entityModelDetails: fragmentDetails.inputDetails,
        key: `${bin.binId}-input`,
        requiresMultiLink: false,
      },
    ];

    if (saveFragments) {
      options.push({
        type: 'single-option' as const,
        displayName: 'Amplified fragments',
        value: {type: FieldMappingType.OUTPUT_FRAGMENT_BIN, binId: bin.binId},
        entityModelDetails: fragmentDetails.outputDetails,
        key: `${bin.binId}-output`,
        requiresMultiLink: false,
      });
    }

    return {
      type: 'nested-option' as const,
      color: bin.binColor,
      displayName: bin.binName,
      key: bin.binId,
      options,
    };
  });

  const aggregatedOptions: SectionOptions<FieldMappingTarget> = [
    {
      type: 'single-option' as const,
      displayName: 'All input fragments',
      value: {type: FieldMappingType.ALL_INPUT_FRAGMENTS},
      entityModelDetails: mergeAssemblyEntityCountsByModelType(
        orderedBinDetails.map((bin) => fragmentDetailsByBin[bin.binId].inputDetails)
      ),
      key: 'all-input',
      requiresMultiLink: true,
    },
  ];

  if (saveFragments) {
    aggregatedOptions.push({
      type: 'single-option' as const,
      displayName: 'All amplified fragments',
      value: {type: FieldMappingType.ALL_OUTPUT_FRAGMENTS},
      entityModelDetails: mergeAssemblyEntityCountsByModelType(
        orderedBinDetails.map((bin) => fragmentDetailsByBin[bin.binId].outputDetails)
      ),
      key: 'all-output',
      requiresMultiLink: true,
    });
  }

  return [...nestedOptions, ...aggregatedOptions];
}

function getAdjustedInputDetailsByModelType(
  inputDetails: UnknownInputPolymers,
  schemaCompatibilityResult: SuccessReturnType<SchemaCompatibilityQuery>
): AssemblyEntityCountsByModelType {
  const accessiblePolymersById = groupByUniqueKey(
    accessibleObjs(schemaCompatibilityResult.data.polymers),
    ({id}) => id
  );
  const singleCountsByModelType = inputDetails.polymerIds.map((polymerId: string) => {
    const polymerDetails = accessiblePolymersById[polymerId];
    if (polymerDetails == null) {
      throw new Error('Encountered unreadable polymer');
    }
    let modelType: OligoAwareFolderItemType;
    if (polymerDetails.__typename === 'SequenceNode') {
      modelType = getSequenceSubtype(
        SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP[polymerDetails.sequenceType],
        polymerDetails.nucleotideType
      );
    } else {
      modelType = ModelTypeConstants.PROTEIN;
    }

    if (polymerDetails.tagSchema != null) {
      return {
        [modelType]: {
          unschematizedPolymerCounts: 0,
          polymerCountsBySchemaId: {
            [polymerDetails.tagSchema.id]: 1,
          },
        },
      };
    } else {
      return {
        [modelType]: {
          unschematizedPolymerCounts: 1,
          polymerCountsBySchemaId: {},
        },
      };
    }
  });
  return mergeAssemblyEntityCountsByModelType(singleCountsByModelType);
}

function getAdjustedOutputDetailsByModelType(
  outputDetails: RawOutputPolymerCount,
  tagSchemaId: string | null
): AssemblyEntityCountsByModelType {
  /* Output data has no schema set by default, but the user can specify one. Transform the data based
     on whether a primer schema is being applied or not. In either case, we expect one of the attributes to
     be empty since all output entities will receive the same treatment.
  */
  if (!tagSchemaId) {
    return {
      [outputDetails.modelType]: {
        unschematizedPolymerCounts: outputDetails.count,
        polymerCountsBySchemaId: {},
      },
    };
  } else {
    return {
      [outputDetails.modelType]: {
        unschematizedPolymerCounts: 0,
        polymerCountsBySchemaId: {
          [tagSchemaId]: outputDetails.count,
        },
      },
    };
  }
}

function getSchemaIdToImplementedInterfaces(
  schemaCompatibilityResult: SuccessReturnType<SchemaCompatibilityQuery>
): SchemaIdToImplementedInterfaces {
  const result: SchemaIdToImplementedInterfaces = {};
  for (const {tagSchema} of accessibleObjs(schemaCompatibilityResult.data.polymers)) {
    if (tagSchema != null) {
      result[tagSchema.id] = new Set(tagSchema.interfaces.map(({id}) => id));
    }
  }
  return result;
}

export interface PerformAssemblyDefineOutputsPaneProps {
  orderedBinDetails: Array<BinDetails>;
  constructOutputDetails: RawOutputPolymerCount;
  // Note that this is somewhat duplicated with primerDetailsByPrime, but applies de-duping by bases.
  primerOutputDetails: RawOutputPolymerCount;
  canCreatePrimers: boolean;
  canCreateFragments: boolean;
  fragmentDetailsByBin: Record<string, FragmentDetails>;
  primerDetailsByPrime: Record<
    SequencePrime,
    {preferredPrimers: UnknownInputPolymers; designedPrimers: RawOutputPolymerCount}
  >;
  fragmentIcon: BenchlingIconName;
  dispatch: React.Dispatch<PerformAssemblyModalAction>;
  state: PerformAssemblyModalState;
  schemaCompatibilityResult: SuccessReturnType<SchemaCompatibilityQuery>;
  adjustedFragmentDetailsByBin: Record<
    string,
    {inputDetails: AssemblyEntityCountsByModelType; outputDetails: AssemblyEntityCountsByModelType}
  >;
  totalOutputFragmentCount: number;
}

export default function PerformAssemblyDefineOutputsPane(
  props: PerformAssemblyDefineOutputsPaneProps
): JSX.Element {
  const state = props.state;
  // Load schema interfaces implemented by the tag schemas for compatibility logic.
  const selectedSchemaIdToImplementedInterfaces = useSchemaInterfaces(
    state.constructLocationInfo.tagSchemaId,
    state.fragmentLocationInfo.tagSchemaId,
    state.primerLocationInfo.tagSchemaId
  );

  const schemaIdToImplementedInterfaces = {
    ...selectedSchemaIdToImplementedInterfaces,
    ...getSchemaIdToImplementedInterfaces(props.schemaCompatibilityResult),
  };

  return (
    <div {...getTestProps({component: 'PerformAssemblyDefineOutputsPane', element: 'root'})}>
      <div className={css(styles.modalBody)}>
        <div className={css(styles.description)}>
          Set the schema for each assembly output you want to create and optionally map assembly entities to
          relevant schema fields
        </div>

        <div className={css(styles.outputSectionsContainer)}>
          <OutputSection
            extraSwatchStyles={
              getConstructsSwatchesStyles(props.orderedBinDetails.map(({binColor}) => binColor))
                .constructsSwatches
            }
            title="Constructs"
            icon={ModelTypeConstants.BULK_ASSEMBLY}
            entityCount={props.constructOutputDetails.count}
            entityName="constructs"
          >
            <SchemaSection
              schemaId={state.constructLocationInfo.tagSchemaId}
              onTagSchemaChange={(tagSchema) => {
                props.dispatch({
                  type: 'UPDATE_TAG_SCHEMA',
                  entityType: 'construct',
                  tagSchemaId: tagSchema?.id ?? null,
                });
              }}
              folderItemType={props.constructOutputDetails.modelType}
              testKey="constructs"
            />
          </OutputSection>
          {props.canCreatePrimers && props.primerOutputDetails.count > 0 && (
            <OutputSection
              title="Primers"
              icon={FolderItemIcon.OligoV2}
              entityCount={props.primerOutputDetails.count}
              entityName="sequences"
              switchDetails={{
                checked: state.savePrimers,
                onCheck: () => {
                  props.dispatch({type: 'SET_SAVE_PRIMERS', value: !state.savePrimers});
                },
                tooltipText: 'Create primer sequences',
              }}
            >
              <SchemaSection
                schemaId={state.primerLocationInfo.tagSchemaId}
                onTagSchemaChange={(tagSchema) => {
                  props.dispatch({
                    type: 'UPDATE_TAG_SCHEMA',
                    entityType: 'primer',
                    tagSchemaId: tagSchema?.id ?? null,
                  });
                }}
                disabled={!state.savePrimers}
                folderItemType={props.primerOutputDetails.modelType}
                testKey="primers"
              />
            </OutputSection>
          )}
          {props.canCreateFragments && (
            <OutputSection
              extraSwatchStyles={
                state.selectedFragmentBinId
                  ? getFragmentBinSwatchStyles(
                      props.orderedBinDetails.find(({binId}) => binId === state.selectedFragmentBinId)
                        ?.binColor || null
                    ).fragmentBinSwatch
                  : undefined
              }
              title="Amplified fragments"
              icon={props.fragmentIcon}
              entityCount={props.totalOutputFragmentCount}
              entityName="fragments"
              switchDetails={{
                checked: state.saveFragments,
                onCheck: () => {
                  props.dispatch({type: 'SET_SAVE_FRAGMENTS', value: !state.saveFragments});
                },
                tooltipText: 'Create amplified fragment sequences',
              }}
            >
              <SchemaSection
                schemaId={state.fragmentLocationInfo.tagSchemaId}
                onTagSchemaChange={(tagSchema) => {
                  props.dispatch({
                    type: 'UPDATE_TAG_SCHEMA',
                    entityType: 'fragment',
                    tagSchemaId: tagSchema?.id ?? null,
                  });
                }}
                disabled={!state.saveFragments}
                folderItemType={ModelTypeConstants.SEQUENCE}
                testKey="fragments"
              />
              <BinSection
                orderedBinDetails={props.orderedBinDetails}
                selectedBinId={state.selectedFragmentBinId}
                onBinChange={(binId) => {
                  props.dispatch({type: 'SET_SELECTED_FRAGMENT_BIN_ID', value: binId});
                }}
                disabled={!state.saveFragments || !state.fragmentLocationInfo.tagSchemaId}
              />
            </OutputSection>
          )}
        </div>
      </div>
      <hr className={css(styles.fullWidthDivider)} />
      <div className={css(styles.modalBody)}>
        <div className={css(styles.schemaFieldMappersContainer)}>
          {state.constructLocationInfo.tagSchemaId && (
            <SchemaFieldMapper<FieldMappingTarget | null>
              tagSchemaId={state.constructLocationInfo.tagSchemaId}
              title="Constructs"
              sectionDetails={{
                options: [
                  {
                    type: 'single-option',
                    displayName: 'None',
                    value: null,
                    key: 'none',
                    requiresMultiLink: false,
                  },
                  ...getOptionsForConstructField(
                    props.orderedBinDetails,
                    props.adjustedFragmentDetailsByBin,
                    state.saveFragments
                  ),
                ],
              }}
              schemaIdToImplementedInterfaces={schemaIdToImplementedInterfaces}
              fieldMapping={state.constructLocationInfo.fieldMapping ?? {}}
              onFieldMappingChange={(fieldId, target) => {
                props.dispatch({type: 'UPDATE_FIELD_MAPPING', entityType: 'construct', fieldId, target});
              }}
            />
          )}

          {state.savePrimers && state.primerLocationInfo.tagSchemaId && (
            <SchemaFieldMapper<FieldMappingTarget | null>
              tagSchemaId={state.primerLocationInfo.tagSchemaId}
              title="Primers"
              sectionDetails={{
                options: [
                  {
                    type: 'single-option',
                    displayName: 'None',
                    value: null,
                    key: 'none',
                    requiresMultiLink: false,
                  },
                  {
                    type: 'single-option',
                    displayName: 'Paired primers',
                    value: {type: FieldMappingType.OTHER_PRIMERS},
                    entityModelDetails: mergeAssemblyEntityCountsByModelType([
                      getAdjustedOutputDetailsByModelType(
                        props.primerOutputDetails,
                        state.primerLocationInfo.tagSchemaId
                      ),
                      getAdjustedInputDetailsByModelType(
                        props.primerDetailsByPrime[SequencePrime.FIVE].preferredPrimers,
                        props.schemaCompatibilityResult
                      ),
                      getAdjustedInputDetailsByModelType(
                        props.primerDetailsByPrime[SequencePrime.THREE].preferredPrimers,
                        props.schemaCompatibilityResult
                      ),
                    ]),
                    key: 'other_primers',
                    // This is somewhat nuanced. A multi-link is only required when oligos are de-duplicated or a preferred primer is used multiple times.
                    requiresMultiLink: true,
                  },
                ],
              }}
              schemaIdToImplementedInterfaces={schemaIdToImplementedInterfaces}
              fieldMapping={state.primerLocationInfo.fieldMapping ?? {}}
              onFieldMappingChange={(fieldId, target) => {
                props.dispatch({type: 'UPDATE_FIELD_MAPPING', entityType: 'primer', fieldId, target});
              }}
            />
          )}

          {state.saveFragments && state.fragmentLocationInfo.tagSchemaId && (
            <SchemaFieldMapper<FieldMappingTarget | null>
              tagSchemaId={state.fragmentLocationInfo.tagSchemaId}
              title="Amplified fragments"
              sectionDetails={{
                options: [
                  {
                    type: 'single-option',
                    displayName: 'None',
                    value: null,
                    key: 'none',
                    requiresMultiLink: false,
                  },
                  ...getOptionsForFragmentPrimerField(
                    props.primerOutputDetails,
                    props.primerDetailsByPrime,
                    props.schemaCompatibilityResult,
                    state.primerLocationInfo.tagSchemaId
                  ),
                ],
              }}
              schemaIdToImplementedInterfaces={schemaIdToImplementedInterfaces}
              fieldMapping={state.fragmentLocationInfo.fieldMapping ?? {}}
              onFieldMappingChange={(fieldId, target) => {
                props.dispatch({type: 'UPDATE_FIELD_MAPPING', entityType: 'fragment', fieldId, target});
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  schemaFieldMappersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  countSection: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Colors.GRAY_DARKER,
    marginTop: '6px',
  },
  countText: {
    marginLeft: '4px',
  },
  stepNavContainer: {
    padding: '0 16px 12px',
  },
  modalBody: {
    padding: '16px',
  },
  description: {
    ...FONT_PROXIMA_14,
    marginBottom: '24px',
  },
  outputSectionsContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    justifyContent: 'flex-start',
  },
  swatch: {
    height: '4px',
    borderRadius: '4px 4px 0px 0px',
    background: Colors.GRAY_DARK,
  },
  outputSection: {
    flex: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderTop: 'none',
    borderRadius: '4px',
    alignSelf: 'flex-start',
    maxWidth: '230px',
  },
  disabledOutputSection: {
    // @ts-expect-error: Aphrodite doesn't recognize this triple format
    borderStyle: 'none dashed dashed',
  },
  fullWidthDivider: {
    border: 0,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    margin: 0,
    width: '100%',
  },
  outputSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  outputSectionTitle: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  outputDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  dropdownSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dropdownTitle: {
    color: Colors.NEAR_BLACK,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  outputSectionContent: {
    padding: '8px 12px',
  },
  fragmentDropdown: {
    borderLeftStyle: 'solid',
    borderLeftWidth: '4px',
  },
});
